import React, { useState, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Text, Button, Divider, Link
} from 'components/ui';
import Grid from 'components/Grid/Grid';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import AppliedGiftCards from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/AppliedGiftCards';
import Apple from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Apple/Apple';
import Paypal from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paypal/Paypal';
import Klarna from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Klarna/Klarna';
import Venmo from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Venmo/Venmo';
import { AfterPayPill } from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AfterPay';
import Paze from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/Paze/Paze';
import GooglePay from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/GooglePay/GooglePay';
import ApplePay from 'services/ApplePay';
import GooglePayUtils from 'utils/GooglePay';
import StringUtils from 'utils/String';
import pazeUtils from 'utils/Paze';
import localeUtils from 'utils/LanguageLocale';
import checkoutUtils from 'utils/Checkout';
import OrderUtils from 'utils/Order';
import paymentUtils from 'utils/payment';
import anaConsts from 'analytics/constants';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import ErrorMsg from 'components/ErrorMsg';
import AccountCredit from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AccountCredit/AccountCredit';
import agentAwareUtils from 'utils/AgentAware';
import useIsSMUI from 'hooks/useIsSMUI';

/**
 * NewUserPaymentView - Payment section for new users with no saved payment method
 * Shows "Add New Card" button, "Use a Gift Card" link, and "Other Ways to Pay" section as pill buttons
 * @param {Object} props - Component props
 * @param {Object} props.localization - Localized strings
 * @param {Object} props.availablePaymentMethods - Available payment method flags
 * @param {Object} props.paymentOptions - Payment options including defaultPayment
 * @param {Object} props.orderDetails - Order details for Apple Pay
 * @param {string} props.installmentValue - Pre-calculated installment value from parent
 * @param {Function} props.onAddNewCard - Handler for opening credit card form
 * @param {Function} props.handleAfterPayExpand - Callback to expand AfterPay view (navigates to L2/L3 state)
 * @param {boolean} props.disableContainerPadding - Disable container padding for reuse in edit view
 * @param {Function} props.handleKlarnaExpand - Callback to expand Klarna view (navigates to L2/L3 state)
 * ([SHP-3366] Implement the new Klarna flow for fric check rel 4)
 */

const getOrderedPillRow = ({
    isApplePaySupported,
    isMobile,
    applePill,
    googlePayPill,
    paypalPill,
    venmoPill,
    klarnaPill,
    afterpayPill,
    pazePill
}) => {
    if (isApplePaySupported) {
        return [applePill, googlePayPill, paypalPill, venmoPill, klarnaPill, afterpayPill, pazePill];
    }

    if (!isMobile) {
        return [googlePayPill, paypalPill, venmoPill, klarnaPill, afterpayPill, pazePill];
    }

    return [googlePayPill, paypalPill, klarnaPill, afterpayPill, venmoPill, pazePill];
};

// eslint-disable-next-line complexity
const NewUserPaymentView = ({
    localization,
    availablePaymentMethods = {},
    _paymentOptions = {},
    orderDetails = {},
    installmentValue,
    onAddNewCard,
    handleAfterPayExpand,
    disableContainerPadding = false,
    withGiftCard = true,
    giftCardPaymentGroups = [],
    showGiftCardAdditionalPaymentRequired = false,
    giftCardAmountEqualsOrderTotal = false,
    onGiftCardSaveAndContinue,
    handleKlarnaExpand,
    handleVenmoExpand,
    handlePazeExpand,
    handlePayWithApplePayClick,
    handlePayWithGooglePayClick,
    handlePayPalClick,
    physicalGiftCardShippingGroup,
    onAlternatePaymentDetails,
    isSubscriptionOrder,
    hasNoSavedCreditCards,
    paymentSectionHasError,
    storeCredits = [],
    isFullyCoveredByAccountCredit = false,
    hasSavedAlternatePayments = false,
    paddingTop = [4, 5]
}) => {
    const [isGiftCardFormOpen, setIsGiftCardFormOpen] = useState(false);
    const [isApplePaySupported, setIsApplePaySupported] = useState(false);
    const [isGooglePaySupported, setIsGooglePaySupported] = useState(false);
    const isMobile = useIsSMUI();
    const hasTrackedButtonStateRef = useRef(false);
    const hasTrackedUnavailableRef = useRef(false);

    useEffect(() => {
        ApplePay.checkApplePayments().then(payType => {
            setIsApplePaySupported(payType !== ApplePay.TYPES.HIDDEN);
        });
    }, []);

    const {
        isPayPalEnabled,
        isVenmoEnabledForThisOrder,
        isKlarnaEnabledForThisOrder,
        isAfterpayEnabledForThisOrder,
        isAfterpayEnabledForThisProfile,
        isPazeEnabledForThisOrder,
        isPayPalPayLaterEligible,
        isGooglePayEnabledForThisOrder,
        // Default to true so callers that don't build this flag preserve the pre-split behavior
        isApplePayEnabledForThisOrder = true
    } = availablePaymentMethods;

    useEffect(() => {
        if (!isGooglePayEnabledForThisOrder) {
            setIsGooglePaySupported(false);

            return undefined;
        }

        let isCancelled = false;

        (async () => {
            try {
                await GooglePayUtils.initPaymentsClient();

                if (!isCancelled) {
                    setIsGooglePaySupported(true);
                }
            } catch (err) {
                Sephora.logger.error('[GooglePay] initPaymentsClient failed', err);
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [isGooglePayEnabledForThisOrder]);

    const hasAlternatePayments =
        isPayPalEnabled ||
        isVenmoEnabledForThisOrder ||
        isKlarnaEnabledForThisOrder ||
        isAfterpayEnabledForThisOrder ||
        isPazeEnabledForThisOrder ||
        // Include Google Pay so a GPay-only scenario doesn't collapse the entire
        // "Other Ways to Pay" section (which would also hide the pill itself).
        isGooglePaySupported;
    const hasAppliedGiftCards = giftCardPaymentGroups.length > 0;
    const hasReachedMaxGiftCards = giftCardPaymentGroups.length >= 2;
    // Subscription orders are an exception: show the gift card control even when account credit fully covers the order
    const shouldShowUseGiftCardControl =
        !giftCardAmountEqualsOrderTotal && !hasReachedMaxGiftCards && (!isFullyCoveredByAccountCredit || isSubscriptionOrder);

    // For subscription orders (SDU/AutoReplenish), always show Add New Card button even when
    // the order is fully covered by gift card or account credit, because a credit card is required for future charges
    // For new users with no saved credit cards, always show the button so they can add a card for future purchases
    const shouldShowAddNewCardButton =
        onAddNewCard &&
        ((!giftCardAmountEqualsOrderTotal && !isFullyCoveredByAccountCredit) ||
            isSubscriptionOrder ||
            (hasNoSavedCreditCards && !isFullyCoveredByAccountCredit));

    // Error message when there is no added credit card for subscription order
    const creditCardRequiredMessage =
        checkoutUtils.getCreditCardReplenishmentRequiredMessage(orderDetails?.paymentGroups?.paymentMessages) ||
        checkoutUtils.getCreditCardRequiredMessage(orderDetails?.paymentGroups?.paymentMessages);
    const shouldShowCreditCardRequiredMessage =
        creditCardRequiredMessage && giftCardAmountEqualsOrderTotal && paymentSectionHasError && !isSubscriptionOrder;

    // For subscription orders without saved cards, don't show "Save and Continue" button
    // because there's no payment method to save yet
    const shouldShowGiftCardSaveButton =
        giftCardAmountEqualsOrderTotal && onGiftCardSaveAndContinue && !(isSubscriptionOrder && hasNoSavedCreditCards);

    // Build dynamic list of installment payment methods (only PayPal, Klarna, AfterPay offer installments)
    const installmentMethods = [];

    if (isPayPalPayLaterEligible) {
        installmentMethods.push('PayPal');
    }

    if (isKlarnaEnabledForThisOrder) {
        installmentMethods.push('Klarna');
    }

    if (isAfterpayEnabledForThisOrder && isAfterpayEnabledForThisProfile) {
        installmentMethods.push('Afterpay');
    }

    // Format payment method list: "PayPal, Klarna and AfterPay" or "Klarna and AfterPay"
    const getPaymentMethodsList = () => {
        if (installmentMethods.length === 0) {
            return '';
        }

        if (installmentMethods.length === 1) {
            return installmentMethods[0];
        }

        if (installmentMethods.length === 2) {
            return `${installmentMethods[0]} and ${installmentMethods[1]}`;
        }

        return `${installmentMethods.slice(0, -1).join(', ')} and ${installmentMethods[installmentMethods.length - 1]}`;
    };

    // Show installment message only if we have installment value and at least one installment method
    // Hide Installment message when we have gift cards applied in order
    const showInstallmentMessage = installmentValue && installmentMethods.length > 0 && !hasAppliedGiftCards;

    const isPazeEligible = pazeUtils.isPazeVisible();
    const isApplePayUnavailable =
        !giftCardAmountEqualsOrderTotal && (!isApplePayEnabledForThisOrder || orderDetails?.header?.isApplePayEnabled === false);
    const isAfterpayUnavailable =
        !giftCardAmountEqualsOrderTotal && (!isAfterpayEnabledForThisOrder || !isAfterpayEnabledForThisProfile);
    const unavailableMethods = [];

    // Show ApplePay as unavailable only on devices that support Apple Pay
    if (isApplePayUnavailable && isApplePaySupported) {
        unavailableMethods.push('Apple Pay');
    }

    if (!isPayPalEnabled) {
        unavailableMethods.push('PayPal');
    }

    if (!isKlarnaEnabledForThisOrder) {
        unavailableMethods.push('Klarna');
    }

    if (isAfterpayUnavailable) {
        unavailableMethods.push('Afterpay');
    }

    if (!isVenmoEnabledForThisOrder && !localeUtils.isCanada()) {
        unavailableMethods.push('Venmo');
    }

    if (isPazeEligible && !isPazeEnabledForThisOrder) {
        unavailableMethods.push('Paze');
    }

    const shouldShowUnavailableMethods = unavailableMethods.length > 0;
    // When a gift card fully covers the order there is nothing left to pay with, so the alternate
    // payment pills and the accompanying unavailable-methods message add no value and only produce
    // misleading copy (e.g. "cannot be combined with gift cards"). Subscription orders are excepted
    // because a saved payment method is still required for future recurring charges.
    const hideAlternatePaymentsForFullGCCoverage = giftCardAmountEqualsOrderTotal && !isSubscriptionOrder;

    const renderUnavailableMethods = methods =>
        methods.map((method, index) => {
            const isLast = index === methods.length - 1;
            const isSecondLast = index === methods.length - 2;
            const separator = methods.length === 2 || isSecondLast ? localization.and : ', ';

            return (
                <React.Fragment key={method}>
                    <Text fontWeight='bold'>{method}</Text>
                    {!isLast && separator}
                </React.Fragment>
            );
        });

    const handleAlternatePaymentDetails = event => {
        event.preventDefault();
        FrictionlessCheckoutBindings.setAltPaymentDetailsModalAnalytics();
        onAlternatePaymentDetails && onAlternatePaymentDetails();
    };

    // Determine which message to show for unavailable payment methods
    const getUnavailablePaymentMessage = () => {
        if (hasAppliedGiftCards && paymentUtils.hasGiftCardBlockedMethod(unavailableMethods)) {
            return localization.cannotBeCombinedWithGiftCards;
        }

        if (physicalGiftCardShippingGroup) {
            return localization.cannotBeUsedWhenPurchasingGiftCards;
        }

        return localization.cantUseForThisOrder;
    };

    // Build plain-text version of the unavailable methods list for analytics (eVar140)
    const getUnavailableMethodsPlainText = methods => {
        if (methods.length === 0) {
            return '';
        }

        if (methods.length === 1) {
            return methods[0];
        }

        if (methods.length === 2) {
            return `${methods[0]} and ${methods[1]}`;
        }

        return `${methods.slice(0, -1).join(', ')} and ${methods[methods.length - 1]}`;
    };

    // Set Page Name for sync PAGE_LOAD call on component mount for the payment section state
    // for case when we don't have credit card and saved alternate payment methods
    useEffect(() => {
        if (shouldShowAddNewCardButton && !hasSavedAlternatePayments && hasNoSavedCreditCards) {
            if (!hasTrackedButtonStateRef.current && !digitalData.page.attributes.initialPageLoadDidOccur) {
                hasTrackedButtonStateRef.current = true;

                FrictionlessCheckoutBindings.setPaymentPageNameForInitialLoad(anaConsts.ACTION_INFO.PAYMENT_ADD_NEW_CARD);
            }
        }
    }, []);

    // Fire a single ASYNC_PAGE_LOAD for the payment section state, combining:
    // - button CTA suffix (e.g., *button=add new card)
    // - unavailable payment methods message (eVar140)
    // Skip when saved alternate payments exist (SavedAlternatePaymentRadio tracks "use this payment method")
    // or when saved credit cards exist (trackUseThisCardButtonState tracks "use this card").
    useEffect(() => {
        if (shouldShowAddNewCardButton && !hasSavedAlternatePayments && hasNoSavedCreditCards) {
            if (!hasTrackedButtonStateRef.current) {
                hasTrackedButtonStateRef.current = true;

                const unavailableMessage = shouldShowUnavailableMethods && !hideAlternatePaymentsForFullGCCoverage
                    ? `${getUnavailableMethodsPlainText(unavailableMethods)} ${getUnavailablePaymentMessage()}`.trim()
                    : '';

                FrictionlessCheckoutBindings.setPaymentSectionButtonStateAnalytics(anaConsts.ACTION_INFO.PAYMENT_ADD_NEW_CARD, unavailableMessage);
            }
        } else if (
            shouldShowUnavailableMethods &&
            !hideAlternatePaymentsForFullGCCoverage &&
            !hasTrackedUnavailableRef.current &&
            hasNoSavedCreditCards
        ) {
            hasTrackedUnavailableRef.current = true;
            FrictionlessCheckoutBindings.setAltPaymentUnavailableAnalytics(
                `${getUnavailableMethodsPlainText(unavailableMethods)} ${getUnavailablePaymentMessage()}`.trim()
            );
        }
    }, [shouldShowAddNewCardButton, hasSavedAlternatePayments, hasNoSavedCreditCards]);

    // Hoisted out of the JSX so the Grid stays declarative. Ordering rules:
    //   AC1 Apple Pay available (Desktop + MWeb): [Apple, GPay, PayPal, Venmo, Klarna, AfterPay, Paze]
    //   AC2 Apple Pay NOT available (Desktop):    [GPay, PayPal, Venmo, Klarna, AfterPay, Paze]
    //   AC3 Apple Pay NOT available (MWeb):       [GPay, PayPal, Klarna, AfterPay, Venmo, Paze]

    const transactionAmount = orderDetails?.priceInfo?.creditCardAmount || orderDetails?.priceInfo?.orderTotal || 0;
    const isBopis = orderDetails?.header?.isBopisOrder;

    const applePill = (
        <Apple
            key='apple'
            isApplePayEnabledForThisOrder={isApplePayEnabledForThisOrder}
            isPayWithApplePay={false}
            handlePayWithApplePayClick={handlePayWithApplePayClick}
            orderDetails={orderDetails}
        />
    );
    const googlePayPill = isGooglePaySupported && (
        <GooglePay
            key='gpay'
            transactionAmount={transactionAmount}
            isBopis={isBopis}
            handleGooglePayClick={handlePayWithGooglePayClick}
        />
    );
    const paypalPill = (
        <Paypal
            key='paypal'
            isPayPalEnabled={isPayPalEnabled}
            handlePaymentClick={handlePayPalClick}
        />
    );
    const venmoPill = (
        <Venmo
            key='venmo'
            isVenmoEnabledForThisOrder={isVenmoEnabledForThisOrder}
            handleVenmoExpand={handleVenmoExpand}
        />
    );
    const klarnaPill = (
        <Klarna
            key='klarna'
            isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
            handlePaymentClick={handleKlarnaExpand}
        />
    );
    const afterpayPill = (
        <AfterPayPill
            key='afterpay'
            isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
            isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
            handleAfterPayExpand={handleAfterPayExpand}
        />
    );
    const pazePill = (
        <Paze
            key='paze'
            isPazeEnabledForThisOrder={isPazeEnabledForThisOrder}
            handlePaymentClick={handlePazeExpand}
        />
    );

    const orderedPills = getOrderedPillRow({
        isApplePaySupported,
        isMobile,
        applePill,
        googlePayPill,
        paypalPill,
        venmoPill,
        klarnaPill,
        afterpayPill,
        pazePill
    });

    return (
        <Box
            paddingX={disableContainerPadding ? 0 : [4, 4, 5]}
            paddingTop={disableContainerPadding ? 0 : paddingTop}
            paddingBottom={
                disableContainerPadding || (shouldShowUnavailableMethods && (!isFullyCoveredByAccountCredit || isSubscriptionOrder)) ? 0 : [1, 2]
            }
            data-at={Sephora.debug.dataAt('new_user_payment_view')}
        >
            {/* SHP-4190: Display error message when account credit covers full order */}
            {isFullyCoveredByAccountCredit && !(isSubscriptionOrder && hasNoSavedCreditCards) && (
                <ErrorMsg
                    mt={0}
                    mb={0}
                    backgroundColor='white'
                    padding={1}
                    color='red'
                    borderRadius={2}
                    display='flex'
                    alignItems='center'
                    fontSize='base'
                    role='alert'
                    aria-live='polite'
                >
                    <span>{localization.accountCreditCoversFullOrder}</span>
                </ErrorMsg>
            )}

            {/* Account credit displayed first, before other payment methods */}
            {storeCredits?.length > 0 && (
                <>
                    {storeCredits.map((storeCredit, index) => (
                        <React.Fragment key={`storeCredits_${index.toString()}`}>
                            <AccountCredit
                                withCheckbox={false}
                                storeCredit={storeCredit}
                                locales={{
                                    accountCreditAltText: localization.accountCredit,
                                    storeCreditApplied: localization.accountCredit,
                                    expires: localization.expires,
                                    accountCreditApplied: localization.accountCreditApplied
                                }}
                                marginBottom={4}
                            />
                            {index !== storeCredits.length - 1 && <Divider marginBottom={4} />}
                        </React.Fragment>
                    ))}
                    {!isFullyCoveredByAccountCredit && <Divider marginBottom={4} />}
                </>
            )}

            {/* Add New Card Button */}
            {shouldShowAddNewCardButton && (
                <Box className={agentAwareUtils.applyHideAgentAwareClassToTiers(['1', '2'])}>
                    <Button
                        variant='primary'
                        width={['100%', '297px', '50%']}
                        justifyContent='center'
                        onClick={onAddNewCard}
                        aria-label={localization.addNewCard}
                        data-at={Sephora.debug.dataAt('add_new_card_button')}
                        children={localization.addNewCard}
                    />
                </Box>
            )}

            {/* Save and Continue button when account credit covers full order */}
            {isFullyCoveredByAccountCredit && onGiftCardSaveAndContinue && !(isSubscriptionOrder && hasNoSavedCreditCards) && (
                <Button
                    variant='primary'
                    width={['100%', '100%', '380px']}
                    onClick={onGiftCardSaveAndContinue}
                    data-at={Sephora.debug.dataAt('save_and_continue_account_credit')}
                >
                    {localization.afterpaySaveAndContinue}
                </Button>
            )}

            {/* Hide gift card section entirely when account credit covers full order and no applied gift cards (subscription orders are exempt) */}
            {withGiftCard && (!isFullyCoveredByAccountCredit || hasAppliedGiftCards || isSubscriptionOrder) && (
                <>
                    {/* Divider before Gift Card, hidden for Tier 1/2 agents since "Add New Card" above is also hidden */}
                    {(!isFullyCoveredByAccountCredit || isSubscriptionOrder) && (
                        <Box className={agentAwareUtils.applyHideAgentAwareClassToTiers(['1', '2'])}>
                            <Divider marginY={4} />
                        </Box>
                    )}

                    {hasAppliedGiftCards && (
                        <>
                            <Text
                                is='h3'
                                fontWeight='bold'
                            >
                                {localization.giftCard}
                            </Text>
                            {showGiftCardAdditionalPaymentRequired && (
                                <Text
                                    is='p'
                                    color='red'
                                    marginBottom={[2, 3]}
                                    marginTop={[2, 3]}
                                    lineHeight='tight'
                                >
                                    {localization.giftCardAdditionalPaymentRequired}
                                </Text>
                            )}
                            {shouldShowCreditCardRequiredMessage && (
                                <Text
                                    is='p'
                                    color='red'
                                    marginBottom={[2, 3]}
                                    marginTop={[2, 3]}
                                    lineHeight='tight'
                                >
                                    {localization.accountCreditSubscriptionMessage}
                                </Text>
                            )}
                            <AppliedGiftCards />
                            {shouldShowGiftCardSaveButton && (
                                <Button
                                    variant='primary'
                                    marginTop={4}
                                    width={['100%', '296px']}
                                    onClick={onGiftCardSaveAndContinue}
                                >
                                    {localization.afterpaySaveAndContinue}
                                </Button>
                            )}
                        </>
                    )}

                    {shouldShowUseGiftCardControl &&
                        (physicalGiftCardShippingGroup && OrderUtils.isGiftCardOnly() ? (
                            <Text
                                is='p'
                                color='gray'
                                fontSize='sm'
                                lineHeight='tight'
                                marginTop={4}
                            >
                                {localization.gcNotAvailableWhenPurchasingGiftCard}
                            </Text>
                        ) : (
                            <UseAGiftCard
                                isNewUserPaymentView={true}
                                withoutLeftMargin={true}
                                hasTitle={!hasAppliedGiftCards}
                                onFormVisibilityChange={setIsGiftCardFormOpen}
                            />
                        ))}

                    {hasReachedMaxGiftCards && (
                        <Text
                            is='p'
                            color='gray'
                            fontSize='sm'
                            lineHeight='tight'
                            marginTop={4}
                        >
                            {localization.maxGiftCards}
                        </Text>
                    )}
                </>
            )}

            {/* Divider before Other Ways to Pay */}
            {(hasAlternatePayments || shouldShowUnavailableMethods) &&
                !isGiftCardFormOpen &&
                !isFullyCoveredByAccountCredit &&
                !hideAlternatePaymentsForFullGCCoverage && (
                <Box className={agentAwareUtils.applyHideAgentAwareClassToTiers(['1', '2'])}>
                    <Divider marginY={[3, 4]} />
                </Box>
            )}

            {/* Other Ways to Pay Section - Pill Button Layout */}
            {/* Hide alternate payments when account credit or a gift card fully covers the order;
                subscription orders remain the exception since they still require a saved payment method. */}
            {hasAlternatePayments && !isGiftCardFormOpen && !isFullyCoveredByAccountCredit && !hideAlternatePaymentsForFullGCCoverage && (
                <Box className={agentAwareUtils.applyHideAgentAwareClass()}>
                    <Text
                        is='h3'
                        fontWeight='bold'
                        marginBottom={[1, 2]}
                        aria-level='3'
                    >
                        {localization.otherWaysToPay}
                    </Text>

                    {/* Interest-free payments description - dynamic based on order amount and available methods */}
                    {showInstallmentMessage && (
                        <Text
                            is='p'
                            lineHeight={['18px', 'none']}
                        >
                            {localization.payWith}
                            <b>{` ${StringUtils.format(localization.installmentsMessage, installmentValue)} `}</b>
                            {`${localization.availableWith} `}
                            {getPaymentMethodsList()}
                        </Text>
                    )}

                    {/* Payment Methods - Using Real UFE Components.
                        Ordering rules:
                          AC1 Apple Pay available (Desktop + MWeb): [Apple, GPay, PayPal, Venmo, Klarna, AfterPay, Paze]
                          AC2 Apple Pay NOT available (Desktop):    [GPay, PayPal, Venmo, Klarna, AfterPay, Paze]
                          AC3 Apple Pay NOT available (MWeb):       [GPay, PayPal, Klarna, AfterPay, Venmo, Paze]
                        The pill array is assembled above (orderedPills) so the JSX
                        stays declarative and the ordering logic is easier to scan. */}
                    <Grid
                        columns={[2, null, 4]}
                        rowGap={4}
                        columnGap={[2, 4]}
                        marginTop={[2, null, 2]}
                        role='group'
                        aria-label='Alternative payment methods'
                    >
                        {orderedPills}
                    </Grid>
                </Box>
            )}
            {/* Hide unavailable payment methods message when account credit or a gift card covers full order, except for subscription orders which still require a credit card */}
            {shouldShowUnavailableMethods && (!isFullyCoveredByAccountCredit || isSubscriptionOrder) && !hideAlternatePaymentsForFullGCCoverage && (
                <Box className={agentAwareUtils.applyHideAgentAwareClassToTiers(['1', '2'])}>
                    <Text
                        is='p'
                        fontSize='sm'
                        marginTop={4}
                        lineHeight='tight'
                    >
                        {renderUnavailableMethods(unavailableMethods)} {getUnavailablePaymentMessage()}{' '}
                        <Link
                            onClick={handleAlternatePaymentDetails}
                            children={localization.seeDetails}
                            color='blue'
                            css={{ textDecoration: 'underline' }}
                        />
                    </Text>
                </Box>
            )}
        </Box>
    );
};

export default wrapFunctionalComponent(NewUserPaymentView, 'NewUserPaymentView');
